import React from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowUpward as SentIcon,
  ArrowDownward as ReceivedIcon,
  SwapHoriz as SwapIcon,
  Receipt as ContractIcon,
  ExpandMore as ExpandIcon,
  OpenInNew as ExplorerIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { ethers } from "ethers";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import { fetchTransactions } from "../../redux/walletSlice";

// Mock transaction data - replace with real API calls

// Helper functions
const formatAmount = (amount: string, decimals: number) => {
  return parseFloat(ethers.formatUnits(amount, decimals)).toFixed(4);
};

const getTransactionType = (type: string) => {
  switch (type) {
    case "sent":
      return { icon: <SentIcon color="error" />, label: "Sent" };
    case "received":
      return { icon: <ReceivedIcon color="success" />, label: "Received" };
    case "swap":
      return { icon: <SwapIcon color="info" />, label: "Swap" };
    default:
      return { icon: <ContractIcon color="warning" />, label: "Contract" };
  }
};

const HistorySection = () => {
  const [loading, setLoading] = React.useState(true);
  const [expandedTx, setExpandedTx] = React.useState<string | null>(null);
  const { transactions } = useAppSelector((state) => state.wallet);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { selectedToken } = useAppSelector((state) => state.wallet);

  // In a real app, you would fetch this data from an API
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [dispatch, user?.walletAddress]);

  React.useEffect(() => {
    // console.log("Fetching transactions for user:", user?.walletAddress);
    if (user?.walletAddress) {
      dispatch(
        fetchTransactions({
          accountAddress: user.walletAddress,
          contractAddress: selectedToken?.address,
        })
      );
    }
  }, [dispatch, user?.walletAddress, selectedToken]);

  const toggleExpand = (txHash: string) => {
    setExpandedTx(expandedTx === txHash ? null : txHash);
  };

  const viewOnExplorer = (txHash: string) => {
    window.open(`https://polygonscan.com/tx/${txHash}`, "_blank");
  };
  const scrollableBoxStyles = {
    maxHeight: "60vh",
    overflowY: "auto",
    scrollbarWidth: "none", // For Firefox
    "&::-webkit-scrollbar": {
      display: "none", // For Chrome, Safari, and Opera
    },
  };

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", p: 2, height: "100vh" }}>
      <Typography variant="h6" gutterBottom>
        Transaction History
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !transactions || transactions?.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">No transactions found</Typography>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2, ...scrollableBoxStyles }}>
          <List disablePadding>
            {transactions &&
              transactions.length > 0 &&
              [...transactions]
                ?.sort((a, b) => b.timestamp - a.timestamp)
                .map((tx, index) => (
                  <React.Fragment key={tx.hash}>
                    <ListItem
                      secondaryAction={
                        <Tooltip title="View on explorer">
                          <IconButton onClick={() => viewOnExplorer(tx.hash)}>
                            <ExplorerIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar>{getTransactionType(tx.type).icon}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body1" component="span">
                              {getTransactionType(tx.type).label} {tx.value}{" "}
                              {tx.tokenSymbol}
                            </Typography>
                            <Chip
                              label={tx.status}
                              size="small"
                              color={
                                tx.status === "success" ? "success" : "warning"
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {/* {tx.type === "swap"
                              ? `${
                                  tx.tokenIn && tx.tokenOut
                                    ? `${formatAmount(
                                        tx.tokenIn.amount,
                                        tx.tokenIn.decimals
                                      )} ${tx.tokenIn.symbol} → ${formatAmount(
                                        tx.tokenOut.amount,
                                        tx.tokenOut.decimals
                                      )} ${tx.tokenOut.symbol}`
                                    : "Swap"
                                }`
                              : `${
                                  tx.token
                                    ? formatAmount(
                                        tx.token.amount,
                                        tx.token.decimals
                                      ) +
                                      " " +
                                      tx.token.symbol
                                    : ""
                                }`}*/}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {formatDistanceToNow(
                                new Date(tx.timestamp * 1000)
                              )}{" "}
                              ago
                            </Typography>
                          </>
                        }
                      />
                      <IconButton onClick={() => toggleExpand(tx.hash)}>
                        <ExpandIcon />
                      </IconButton>
                    </ListItem>
                    {expandedTx === tx.hash && (
                      <Box sx={{ pl: 9, pr: 2, pb: 2 }}>
                        <Typography variant="caption" display="block">
                          <strong>Tx Hash:</strong> {tx.hash}
                        </Typography>
                        {tx.type === "sent" && (
                          <Typography variant="caption" display="block">
                            <strong>To:</strong> {tx.to}
                          </Typography>
                        )}
                        {tx.type === "received" && (
                          <Typography variant="caption" display="block">
                            <strong>From:</strong> {tx.from}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block">
                          <strong>Value:</strong> {tx.value} {tx.tokenSymbol}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Gas Used:</strong> {tx.gasUsed} MATIC
                        </Typography>
                      </Box>
                    )}
                    {index < transactions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default HistorySection;
